import { X } from "lucide-react";
import { AlertDialog as AD, Dialog as D } from "radix-ui";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";

import { ApiError, errorMessage } from "@/api/errors";
import { formError } from "@/hooks/useFormErrors";

import { cx } from "@/lib/cx";

import { Button } from "./Button";
import { restoreFocus, takeMenuTrigger } from "./focusReturn";

/** Focus-return target for dialogs opened from a menu item (see focusReturn.ts). */
function useMenuOrigin(open: boolean) {
  const origin = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) origin.current = takeMenuTrigger();
  }, [open]);
  return origin;
}

/**
 * Radix closes the dialog on Escape from a capture listener on the document, before a focused
 * control sees the key. An open combobox list closes itself first; only the next Escape closes the dialog.
 */
function keepOpenForCombobox(event: KeyboardEvent) {
  if (event.target instanceof Element && event.target.closest('[role="combobox"][aria-expanded="true"]')) event.preventDefault();
}

const overlay = "animate-fade-in fixed inset-0 z-40 bg-overlay";
const panel =
  "animate-pop-in fixed left-1/2 z-50 flex w-[calc(100vw-2rem)] -translate-x-1/2 flex-col " +
  "rounded-lg border border-line bg-surface-raised shadow-lg";
/** Where a dialog sits: near the top, never taller than 80% of the viewport. */
const placement = "top-[12vh] max-h-[80vh]";

type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** xl: wide tables (searching and reviewing many rows); document: a tall viewer whose body fills it (no padding). */
  size?: "sm" | "md" | "lg" | "xl" | "document";
  /** When set, the body is a <form>; Enter submits. */
  /** May be async: a refused request (ApiError) is the form's to show from its mutation state. */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => unknown;
};

/** Modal dialog (Radix): focus trap, Escape to close, focus restored to the trigger. */
export function Dialog({ open, onOpenChange, title, description, children, footer, size = "md", onSubmit }: DialogProps) {
  const width = size === "sm" ? "max-w-sm" : size === "lg" ? "max-w-2xl" : size === "xl" || size === "document" ? "max-w-5xl" : "max-w-lg";
  const origin = useMenuOrigin(open);
  const body = (
    <>
      <div className={size === "document" ? "flex min-h-0 flex-1 flex-col" : "min-h-0 flex-1 overflow-y-auto px-5 py-4"}>{children}</div>
      {footer && <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
    </>
  );
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className={overlay} />
        <D.Content className={cx(panel, width, size === "document" ? "top-[4vh] h-[92vh]" : placement)} onEscapeKeyDown={keepOpenForCombobox} onCloseAutoFocus={(event) => restoreFocus(origin.current, event)}>
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-3.5">
            <div className="min-w-0">
              <D.Title className="text-lg font-semibold text-fg">{title}</D.Title>
              {description ? (
                <D.Description className="mt-0.5 text-sm text-fg-muted">{description}</D.Description>
              ) : (
                <D.Description className="sr-only">{title}</D.Description>
              )}
            </div>
            <D.Close asChild>
              <button type="button" aria-label="Close" className="-mr-1 rounded-md p-1 text-fg-subtle hover:bg-surface-hover hover:text-fg">
                <X aria-hidden className="size-4" />
              </button>
            </D.Close>
          </div>
          {onSubmit ? (
            <form
              noValidate
              className="flex min-h-0 flex-1 flex-col"
              onSubmit={(event) => {
                event.preventDefault();
                // React bubbles events through portals: never let this submit a form the dialog was opened from.
                event.stopPropagation();
                const result = onSubmit(event);
                if (result instanceof Promise) {
                  result.catch((error: unknown) => {
                    if (!(error instanceof ApiError)) throw error;
                  });
                }
              }}
            >
              {body}
            </form>
          ) : (
            body
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

type ConfirmProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  /** The button that closes without acting (default "Cancel"), e.g. "Fix it first". */
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => unknown;
  children?: ReactNode;
  /** Where focus goes after a successful confirm, when the trigger may be gone (e.g. its row was removed). */
  focusAfterConfirm?: () => HTMLElement | null | undefined;
  /** The children show field errors themselves (e.g. a reason field): don't repeat them above the buttons. */
  inlineFieldErrors?: boolean;
};

/** Confirmation for destructive or significant actions (Radix AlertDialog). */
export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, cancelLabel = "Cancel", destructive, onConfirm, children, focusAfterConfirm, inlineFieldErrors }: ConfirmProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const origin = useMenuOrigin(open);
  const confirmed = useRef(false);
  const shownError = inlineFieldErrors ? formError(error) : error;
  return (
    <AD.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) setError(null);
        onOpenChange(next);
      }}
    >
      <AD.Portal>
        <AD.Overlay className={overlay} />
        <AD.Content
          className={cx(panel, placement, "max-w-md")}
          onEscapeKeyDown={keepOpenForCombobox}
          onOpenAutoFocus={() => {
            confirmed.current = false;
          }}
          onCloseAutoFocus={(event) => restoreFocus((confirmed.current && focusAfterConfirm?.()) || origin.current, event)}
        >
          <div className="px-5 pt-4 pb-3">
            <AD.Title className="text-lg font-semibold text-fg">{title}</AD.Title>
            <AD.Description className="mt-1 text-sm text-fg-muted">{description}</AD.Description>
            {children && <div className="mt-3">{children}</div>}
            {shownError ? (
              <p role="alert" className="mt-3 rounded-md border border-danger/30 bg-danger-subtle px-3 py-2 text-sm text-danger-text">
                {errorMessage(shownError)}
              </p>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
            <AD.Cancel asChild>
              <Button disabled={busy}>{cancelLabel}</Button>
            </AD.Cancel>
            <Button
              variant={destructive ? "danger" : "primary"}
              loading={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await onConfirm();
                  confirmed.current = true;
                  onOpenChange(false);
                } catch (err) {
                  setError(err); // stays open and explains why (e.g. 403/409)
                } finally {
                  setBusy(false);
                }
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </AD.Content>
      </AD.Portal>
    </AD.Root>
  );
}
